/**
 * Aperture Ring (Faz 11.2 — OCS DEFAULT_OSNAP_RADIUS_PX parity).
 *
 * A thin pixel-fixed circle around the cursor while running object snaps are
 * on (or a one-shot override is armed): the aperture the classic OSNAP hunts
 * within, drawn at the cursor's ground-plane point. Same architecture as the
 * SnapIndicator: in-scene, `useFrame` + epsilon-guard against demand-render
 * frame churn, size through pixelsToWorld so the on-screen radius stays
 * constant at every zoom.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useSnapStore, pixelsToWorld } from '../tools/snapping/SnapManager';
import { worldAtClient } from '../core/dynamicInput/dynamicInputEngine';
import { UX_COLORS } from '../themes/colors';

/** Re-render threshold for zoom-driven size changes (relative fraction). */
const SIZE_EPSILON = 0.08;

export function ApertureRing() {
    const enabled = useSnapStore((s) => s.enabled);
    const overrideType = useSnapStore((s) => s.overrideType);
    const aperture = useSnapStore((s) => s.aperture);
    const { camera, gl } = useThree();
    const groupRef = useRef<THREE.Group>(null);
    const [radius, setRadius] = useState(0.2);
    const clientRef = useRef<{ x: number; y: number } | null>(null);

    // Cursor tracking rides window capture-phase (same pattern as the
    // crosshair overlay) — no store writes, the frame loop reads the ref.
    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            clientRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('pointermove', onMove, true);
        return () => window.removeEventListener('pointermove', onMove, true);
    }, []);

    const visible = (enabled || !!overrideType) && aperture > 0;

    useFrame(() => {
        const group = groupRef.current;
        if (!group || !visible) return;
        const client = clientRef.current;
        if (!client) return;
        const point = worldAtClient(client.x, client.y);
        if (!point) {
            group.visible = false;
            return;
        }
        const next = pixelsToWorld(aperture, point, camera, gl);
        setRadius((prev) => (
            Math.abs(prev - next) / Math.max(next, 1e-6) > SIZE_EPSILON ? next : prev
        ));
        group.visible = true;
        group.position.set(point.x, point.y, point.z);
    });

    // Unit-radius ring: per-frame positioning scales the group, the geometry
    // is built once.
    const ring = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        for (let i = 0; i <= 40; i++) {
            const a = (i / 40) * Math.PI * 2;
            pts.push(new THREE.Vector3(Math.cos(a), Math.sin(a), 0));
        }
        return pts;
    }, []);

    if (!visible) return null;

    return (
        <group ref={groupRef} visible={false} renderOrder={6}>
            {/* Inner scale group: position stays world-true on the outer
                group while the unit ring scales to the aperture radius. */}
            <group scale={radius}>
                <Line
                    points={ring}
                    color={UX_COLORS.aperture}
                    lineWidth={1}
                    transparent
                    opacity={0.9}
                />
            </group>
        </group>
    );
}

export default ApertureRing;
